import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowForwardIos,
  AssignmentTurnedInOutlined,
  FactCheckOutlined,
  FilterAltOutlined,
  PersonOutline,
  Refresh,
  Search,
  SendOutlined,
  UpdateOutlined,
  AccountBalanceWalletOutlined
} from "@mui/icons-material";
import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import { Navigate, useNavigate } from "react-router-dom";
import SectionCard from "../components/SectionCard";
import StatusChip from "../components/StatusChip";
import { AppButton } from "components/common/buttons";
import { AppSelect, AppTextField, AppYearPicker } from "components/common/inputs";
import { subcontractSummaryService } from "../services/subcontractSummary.service";
import { subcontractApi } from "../services/subcontractApi";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";
import { useAuth } from "context/AuthContext";
import api from "services/api";
import { isAdminUser } from "modules/systems/services/application.service";
import { formatNumber, formatDate } from "utils/formatters";

const PAGE_SIZE = 6;
const CURRENT_YEAR = new Date().getFullYear();

const loadAcceptanceApprovalSummaries = async (options) => {
  const summaries = await subcontractSummaryService.getAcceptanceSummaries(options);
  return Promise.all(summaries.map(async (summary) => {
    try {
      const profiles = await subcontractApi.getAcceptanceApprovalProfilesBySubcontract(summary.subcontractId);
      const sorted = [...profiles].sort((left, right) => {
        const count = Number(right.submitCount || 0) - Number(left.submitCount || 0);
        if (count) return count;
        return new Date(right.submittedAt || right.createdAt || 0) - new Date(left.submittedAt || left.createdAt || 0);
      });
      return { ...summary, approvalCount: profiles.length, latestAcceptanceApproval: sorted[0] || null };
    } catch (error) {
      return { ...summary, approvalCount: 0, latestAcceptanceApproval: null, summaryError: error };
    }
  }));
};

const getField = (item, field) =>
  item?.[field] ?? item?.[`${field.charAt(0).toUpperCase()}${field.slice(1)}`];

const getSignYear = (item) => {
  const signDate = getField(item, "signDate");
  if (!signDate) return null;
  const date = new Date(`${String(signDate).slice(0, 10)}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
};

const configs = {
  payments: {
    title: "Thanh toán quyết định giao khoán",
    description: "Theo dõi số kỳ và tiến độ thanh toán lũy kế của từng giao khoán.",
    placeholder: "Tìm số hoặc tên hồ sơ thanh toán...",
    empty: "Chưa có hồ sơ thanh toán phù hợp",
    icon: <AccountBalanceWalletOutlined />,
    load: (options) => subcontractSummaryService.getPaymentSummaries(options),
  },
  acceptances: {
    title: "Nghiệm thu quyết định giao khoán",
    description: "Theo dõi số kỳ và giá trị nghiệm thu lũy kế của từng giao khoán.",
    placeholder: "Tìm số hoặc tên hồ sơ nghiệm thu...",
    empty: "Chưa có hồ sơ nghiệm thu phù hợp",
    icon: <FactCheckOutlined />,
    load: (options) => subcontractSummaryService.getAcceptanceSummaries(options),
  },
  "acceptance-approvals": {
    title: "Phê duyệt nghiệm thu",
    description: "Theo dõi và xử lý hồ sơ phê duyệt của từng kỳ nghiệm thu.",
    placeholder: "Tìm số hoặc tên hồ sơ nghiệm thu...",
    empty: "Chưa có hồ sơ phê duyệt nghiệm thu phù hợp",
    icon: <AssignmentTurnedInOutlined />,
    load: loadAcceptanceApprovalSummaries,
  },
  approvals: {
    title: "Phê duyệt quyết định giao khoán",
    description: "Theo dõi hồ sơ, người gửi và trạng thái phê duyệt gần nhất.",
    placeholder: "Tìm giao khoán hoặc tiêu đề phê duyệt...",
    empty: "Chưa có giao khoán phê duyệt phù hợp",
    icon: <AssignmentTurnedInOutlined />,
    load: (options) => subcontractSummaryService.getApprovalSummaries(options),
  },
};

function ProgressContent({ item, type }) {
  const payment = type === "payments";
  const rawProgress = payment ? item.paidPercent : item.acceptedPercent;
  const rawRemaining = item.remainingPercent;
  const progress = Number.isFinite(Number(rawProgress)) ? Number(rawProgress) : 0;
  const remainingPercent = Number.isFinite(Number(rawRemaining))
    ? Number(rawRemaining)
    : Math.max(100 - progress, 0);
  const latestName = payment
    ? item.latestProfile?.paymentName
    : item.latestProfile?.acceptanceName;
  const latestDate = payment
    ? item.latestProfile?.paymentDate
    : item.latestProfile?.acceptanceDate;
  const done = payment ? "Đã thanh toán" : "Được nghiệm thu";
  const remaining = payment ? "Chưa thanh toán" : "Chưa nghiệm thu";

  return (
    <>
      <Box className="subcontract-workflow-card__metrics">
        <span><strong>{item.periodCount}</strong><small>{payment ? "Kỳ thanh toán" : "Kỳ nghiệm thu"}</small></span>
        {/* <span><strong>{progress.toFixed(1)}%</strong><small>{done}</small></span>
        <span><strong>{remainingPercent.toFixed(1)}%</strong><small>{remaining}</small></span> */}
        <span><strong>{formatNumber(progress)}%</strong><small>{done}</small></span>
        <span><strong>{formatNumber(remainingPercent)}%</strong><small>{remaining}</small></span>
      </Box>
      <Box className="subcontract-workflow-card__progress">
        {/* <Box><span>{done}</span><strong>{progress.toFixed(1)}%</strong></Box>
        <LinearProgress variant="determinate" value={progress} /> */}
        <Box><span>{done}</span><strong>{formatNumber(progress)}%</strong></Box>
        <LinearProgress variant="determinate" value={progress} />
      </Box>
      <Box className="subcontract-workflow-card__latest">
        <UpdateOutlined />
        <span>
          {latestName
            ? `Gần nhất: ${latestName} - ${formatDate(latestDate)}`
            : `Chưa có ${payment ? "kỳ thanh toán" : "kỳ nghiệm thu"}`}
        </span>
      </Box>
    </>
  );
}

function ApprovalContent({ item }) {
  return (
    <>
      <Box className="subcontract-workflow-card__approval-heading">
        <Box>
          <Typography className="subcontract-workflow-card__approval-name">
            {item.approvalName}
          </Typography>
          <Typography className="subcontract-workflow-card__contract-reference">
            {item.subcontractNumber} - {item.subcontractName}
          </Typography>
        </Box>
        <StatusChip value={item.approvalStatus} />
      </Box>
      <Box className="subcontract-workflow-card__approval-meta">
        <span><PersonOutline />Người gửi: <strong>{item.submittedBy}</strong></span>
        <span><UpdateOutlined />Thời gian gửi: <strong>{formatDate(item.submittedAt)}</strong></span>
        <span><SendOutlined />Số lần gửi: <strong>{item.submitCount}</strong></span>
        <span><AssignmentTurnedInOutlined />Số hồ sơ: <strong>{item.approvalCount}</strong></span>
      </Box>
    </>
  );
}

function AcceptanceApprovalContent({ item }) {
  const profile = item.latestAcceptanceApproval;
  return (
    <>
      <Box className="subcontract-workflow-card__approval-heading">
        <Box>
          <Typography className="subcontract-list-item__number">{item.subcontractNumber}</Typography>
          <Typography className="subcontract-list-item__name">{item.subcontractName}</Typography>
        </Box>
        <StatusChip value={profile?.status || "Chưa gửi"} />
      </Box>
      <Box className="subcontract-workflow-card__approval-meta">
        <span><FactCheckOutlined />Số kỳ nghiệm thu: <strong>{item.periodCount || 0}</strong></span>
        <span><AssignmentTurnedInOutlined />Số hồ sơ phê duyệt: <strong>{item.approvalCount || 0}</strong></span>
        <span><UpdateOutlined />Hồ sơ gần nhất: <strong>{profile?.approvalName || "Chưa gửi phê duyệt"}</strong></span>
      </Box>
    </>
  );
}

function WorkflowCard({ item, type, onClick }) {
  const isApproval = type === "approvals";
  const isAcceptanceApproval = type === "acceptance-approvals";

  return (
    <Box component="button" type="button" className="subcontract-workflow-card" onClick={onClick}>
      <Box className={`subcontract-workflow-card__icon ${type}`}>{configs[type].icon}</Box>
      <Box>
        {isApproval ? (
          <ApprovalContent item={item} />
        ) : isAcceptanceApproval ? (
          <AcceptanceApprovalContent item={item} />
        ) : (
          <>
            <Box className="subcontract-workflow-card__heading">
              <Box>
                <Typography className="subcontract-list-item__number">{item.subcontractNumber}</Typography>
                <Typography className="subcontract-list-item__name">{item.subcontractName}</Typography>
              </Box>
              <StatusChip value={item.status} />
            </Box>
            <ProgressContent item={item} type={type} />
          </>
        )}
        {item.summaryError && (
          <Typography className="subcontract-workflow-card__warning">
            Một phần dữ liệu chi tiết chưa tải được
          </Typography>
        )}
      </Box>
      <ArrowForwardIos className="subcontract-workflow-card__arrow" />
    </Box>
  );
}

export default function SubcontractWorkflowListPage({ type }) {
  const config = configs[type];
  const navigate = useNavigate();
  const permissions = useSubcontractPermissions();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [dataFilter, setDataFilter] = useState("all");
  const [yearRange, setYearRange] = useState({ from: CURRENT_YEAR, to: CURRENT_YEAR });
  const [page, setPage] = useState(1);
  const [allowedSubcontractIds, setAllowedSubcontractIds] = useState(null);

  const canRead =
    type === "acceptances"
      ? permissions.canReadAcceptance
      : type === "acceptance-approvals"
        ? permissions.has(SUBCONTRACT_PERMISSIONS.acceptanceApprovalRead)
      : type === "payments"
        ? permissions.has(SUBCONTRACT_PERMISSIONS.paymentRead)
        : permissions.has(SUBCONTRACT_PERMISSIONS.approvalRead);

  useEffect(() => {
    let active = true;

    const loadAccessibleSubcontracts = async () => {
      if (!user?.userId) {
        if (active) setAllowedSubcontractIds("none");
        return;
      }

      if (isAdminUser(user)) {
        if (active) setAllowedSubcontractIds("all");
        return;
      }

      try {
        const response = await api.get(`/UserSubcontract/user/${user.userId}`, {
          params: { pageSize: 1000, pageNumber: 1 },
        });
        const entries = response?.data?.data || [];
        const ids = entries
          .map((item) => item.subcontractId ?? item.SubcontractId)
          .filter((value) => value != null);

        if (active) {
          setAllowedSubcontractIds(ids.length > 0 ? ids : "none");
        }
      } catch (requestError) {
        if (active) {
          setAllowedSubcontractIds("error");
          setError(
            requestError.response?.data?.message ||
            requestError.message ||
            "Không thể tải danh sách phân quyền giao khoán.",
          );
        }
      }
    };

    setAllowedSubcontractIds(null);
    loadAccessibleSubcontracts();

    return () => {
      active = false;
    };
  }, [user]);

  const loadData = async () => {
    if (permissions.loading || !canRead || allowedSubcontractIds === null) return;

    setLoading(true);
    setError("");

    if (allowedSubcontractIds === "error") {
      setItems([]);
      setLoading(false);
      return;
    }

    if (allowedSubcontractIds === "none") {
      setItems([]);
      setLoading(false);
      return;
    }

    try {
      setItems(await config.load({ subcontractIds: allowedSubcontractIds }));
    } catch (requestError) {
      setItems([]);
      setError(requestError.response?.data?.message || requestError.message || "Không thể tải dữ liệu tổng hợp.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setItems([]);
    setPage(1);
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, permissions.loading, canRead, allowedSubcontractIds]);

  const normalizedRange = useMemo(() => {
    const from = Number(yearRange.from) || CURRENT_YEAR;
    const to = Number(yearRange.to) || from;
    return {
      from: Math.min(from, to),
      to: Math.max(from, to),
    };
  }, [yearRange]);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return items.filter((item) => {
      const searchable = [
        item.subcontractNumber,
        item.subcontractName,
        item.approvalName,
        item.submittedBy,
      ].some((value) => String(value || "").toLowerCase().includes(keyword));
      const count = (type === "approvals" || type === "acceptance-approvals") ? item.approvalCount : item.periodCount;
      const matchesData = dataFilter === "all"
        || (dataFilter === "with-data" && count > 0)
        || (dataFilter === "without-data" && count === 0);
      const signYear = getSignYear(item);
      const matchesYear = signYear >= normalizedRange.from && signYear <= normalizedRange.to;
      return (!keyword || searchable) && matchesData && matchesYear;
    });
  }, [dataFilter, items, normalizedRange, search, type]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const displayed = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const totalPeriods = filtered.reduce(
    (sum, item) => sum + ((type === "approvals" || type === "acceptance-approvals") ? item.approvalCount : item.periodCount),
    0,
  );
  const contractsWithData = filtered.filter(
    (item) => ((type === "approvals" || type === "acceptance-approvals") ? item.approvalCount : item.periodCount) > 0,
  ).length;

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const handleYearChange = (field, value) => {
    setYearRange((current) => ({ ...current, [field]: Number(value) || CURRENT_YEAR }));
    setPage(1);
  };

  if (!permissions.loading && !canRead) {
    return <Navigate to="/unauthorized" replace />;
  }

  return (
    <Box className="subcontract-page">
      <Box className="subcontract-filter-bar">
        <FilterAltOutlined color="action" />
        <AppTextField
          placeholder={config.placeholder}
          value={search}
          onChange={(value) => { setSearch(value); setPage(1); }}
          sx={{ flex: 1 }}
          startIcon={<Search fontSize="small" />}
        />
        <AppYearPicker
          label="Từ năm"
          value={yearRange.from}
          onChange={(value) => handleYearChange("from", value)}
          sx={{ minWidth: 120, width: 140 }}
        />
        <AppYearPicker
          label="Đến năm"
          value={yearRange.to}
          onChange={(value) => handleYearChange("to", value)}
          sx={{ minWidth: 120, width: 140 }}
        />
        <AppSelect
          label="Dữ liệu"
          value={dataFilter}
          sx={{ minWidth: 210 }}
          options={[
            { value: "all", label: "Tất cả giao khoán" },
            { value: "with-data", label: "Đã có dữ liệu" },
            { value: "without-data", label: "Chưa có dữ liệu" },
          ]}
          onChange={(value) => { setDataFilter(value); setPage(1); }}
        />
        <Tooltip title="Tải lại dữ liệu"><AppButton color="inherit" onClick={loadData}><Refresh /></AppButton></Tooltip>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <SectionCard title={`${filtered.length} quyết định giao khoán`}>
        {loading ? (
          <Box className="subcontract-list-loading"><CircularProgress size={30} /></Box>
        ) : displayed.length ? (
          <Box className="subcontract-contract-list">
            {displayed.map((item) => (
              <WorkflowCard
                key={item.subcontractId}
                item={item}
                type={type}
                onClick={() => navigate(`/subcontracts/${type}/${item.subcontractId}`)}
              />
            ))}
            <Box className="subcontract-list-pagination">
              <Typography variant="body2" color="text.secondary">Trang {page}/{pageCount}</Typography>
              <Pagination page={page} count={pageCount} color="primary" onChange={(_, value) => setPage(value)} />
            </Box>
          </Box>
        ) : (
          <Box className="subcontract-list-empty">
            {config.icon}
            <Typography fontWeight={700}>{config.empty}</Typography>
            <Typography variant="body2" color="text.secondary">Hãy thử thay đổi từ khóa hoặc bộ lọc dữ liệu</Typography>
          </Box>
        )}
      </SectionCard>
    </Box>
  );
}
